import { z } from 'zod';
import type { WorkOverviewDto, WorkSourceDto } from './work';

/**
 * Raccolta dei dati (ADR 0043): finita la configurazione guidata, le fonti che ogni azienda ha, in quest'ordine.
 * L'ordine è fisso; che cosa si chiede dentro ogni passo lo decide il sistema da ciò che sa di quell'azienda.
 */
export const COLLECTION_STEPS = ['posta', 'fatture', 'documenti', 'programmi'] as const;
export type CollectionStepKey = (typeof COLLECTION_STEPS)[number];

export const COLLECTION_TITLES: Record<CollectionStepKey, string> = {
  posta: 'Posta',
  fatture: 'Fatture dei fornitori',
  documenti: 'Documenti dell’azienda',
  programmi: 'Programmi',
};

/** Ciò che il sistema propone in un passo, ricavato dai dati dell'azienda (mai inventato: ogni voce ha la fonte). */
export interface CollectionProposal {
  /** casella: un indirizzo da collegare; programma: un programma da collegare; carica: file da caricare; fatto: risulta già. */
  tipo: 'casella' | 'programma' | 'carica' | 'fatto';
  titolo: string;
  perche: string;
  /** Indirizzo della casella o chiave del programma. */
  valore: string | null;
  fonte: string;
}

export interface CollectionGuide {
  /** Cosa ha trovato il sistema e cosa chiede, in due o tre frasi. */
  messaggio: string;
  proposte: CollectionProposal[];
  /** Solo per le fatture: i fornitori che risultano dalle fonti. */
  fornitori: Array<{ nome: string; cosa: string; documenti: number; contratto: 'trovato' | 'non_trovato'; fonte: string }>;
  /** Solo per i documenti: cosa manca ancora per questa azienda. */
  mancano: Array<{ titolo: string; perche: string; fonte: string }>;
}

export interface CollectionStepDto {
  chiave: CollectionStepKey;
  numero: number;
  titolo: string;
  stato: 'da_fare' | 'in_corso' | 'fatto' | 'saltato';
  dettaglio: string;
  guida: CollectionGuide | null;
  /** La guida si sta scrivendo (o riscrivendo con dati nuovi). */
  preparando: boolean;
  errore: string | null;
  /** Le fonti di questo passo nella coda di lavorazione, con la loro barra. */
  fonti: WorkSourceDto[];
}

/** Un programma trovato nelle fonti, da configurare uno alla volta. */
export interface CollectionProgramDto {
  id: string;
  key: string;
  nome: string;
  /** Da dove risulta (citazioni, fatture, documenti). */
  da_dove: string[];
  stato: 'da_valutare' | 'collegabile' | 'non_collegabile' | 'collegato' | 'non_usato' | 'non_ora';
  /** Come si collega, in breve, dalla valutazione. */
  come: string | null;
  sincronizzazione: WorkSourceDto | null;
}

export interface CollectionDto {
  /** La configurazione guidata è finita: la raccolta è il passo successivo. */
  disponibile: boolean;
  passi: CollectionStepDto[];
  consigliato: CollectionStepKey | null;
  programmi: CollectionProgramDto[];
  lavori: WorkOverviewDto['active'];
}

export const CollectionMoveSchema = z.object({
  passo: z.enum(COLLECTION_STEPS),
  /** salta: «non ora»; riprendi: torna da fare; fatto: la persona dice che è a posto (es. le fatture arrivano già sulla PEC). */
  azione: z.enum(['salta', 'riprendi', 'fatto']).optional(),
  /** Per i programmi: come la persona decide su uno di loro. */
  programma: z.string().max(200).optional(),
  scelta: z.enum(['non_usato', 'non_ora', 'riprendi']).optional(),
});
export type CollectionMoveInput = z.infer<typeof CollectionMoveSchema>;
